import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowRight, ArrowUpRight, ArrowDown, Star, Sparkles, Crown, Gift } from "lucide-react";
import { Reveal, TextReveal } from "@/components/Reveal";
import Seo from "@/components/Seo";
import Label from "@/components/Label";
import { SERVICES, TEAM, OFFERS, BUSINESS } from "@/data/content";

const OFFER_ICONS = [Sparkles, Star, Crown, Gift];

export default function Home() {
  return (
    <div data-testid="home-page">
      <Seo
        path="/"
        title="Facials, Laser Hair Removal & Korean Skincare in Barrhaven"
        description="Barrhaven Beauty Lounge in Nepean, Ottawa — custom facials, laser hair removal, waxing, threading and advanced Korean skincare. 10,000+ happy clients, 4.3/5 Google rating. Book online today."
      />
      {/* ---------------- HERO (text + CTAs only) ---------------- */}
      <section className="relative">
        <div className="mx-auto flex max-w-[1000px] flex-col items-center px-5 md:px-10 pt-8 pb-10 md:pt-14 md:pb-14 text-center">
          <Reveal delay={0.1}>
            <div className="flex flex-wrap items-center justify-center gap-3" data-testid="hero-trust">
              <span className="inline-flex items-center gap-2 rounded-full bg-[#050505] px-4 py-1.5 text-[11px] uppercase tracking-[0.14em] text-white">
                10,000+ Happy Clients
              </span>
              <span className="inline-flex items-center gap-2 rounded-full border border-[#050505]/25 px-4 py-1.5 text-[11px] uppercase tracking-[0.14em] text-[#050505]">
                <Star size={13} className="fill-[#050505] text-[#050505]" />
                4.3/5 Google Rating
              </span>
            </div>
          </Reveal>
          <h1 className="mt-6 font-serif-display font-normal tracking-tight leading-[0.94] text-[#050505] text-5xl sm:text-6xl md:text-7xl lg:text-8xl">
            <TextReveal
              start={0.15}
              lines={["Beauty,", "self-care &", "quiet luxury."]}
            />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="mt-8 max-w-xl text-base md:text-lg leading-relaxed text-[#3d3d3d]"
          >
            A calm, elegant space in Barrhaven where expert facials, laser hair removal,
            waxing and advanced Korean skincare come together — every treatment tailored to
            you, so you leave refreshed, glowing and confident.
          </motion.p>

        </div>

        {/* Offers & rewards scrolling ribbon */}
        <Link
          to="/offers"
          data-testid="offers-marquee-link"
          className="block border-y border-black/10 bg-[#f5f5f4] py-4 transition-colors duration-300 hover:bg-[#eeeeec]"
        >
          <Marquee speed={45} gradient={false} autoFill>
            {[
              "15% OFF Your First Facial",
              "BOGO 50% On Laser Treatments",
              "Monthly Promotions",
              "Barrhaven Rewards Club",
              "$999 · 12-Session Laser Hair Removal",
            ].map((t, i) => (
              <span
                key={i}
                className="mx-8 font-serif-display text-2xl md:text-3xl italic text-[#050505]"
              >
                {t} <span className="not-italic text-[#bbb]">•</span>
              </span>
            ))}
          </Marquee>
        </Link>

        <div className="mx-auto flex max-w-[1000px] flex-col items-center px-5 md:px-10 pt-6 pb-14 md:pt-8 md:pb-20 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.85, ease: [0.22, 1, 0.36, 1] }}
            className="flex flex-col gap-3 sm:flex-row"
          >
            <a
              href="#services"
              data-testid="hero-services-btn"
              onClick={(e) => {
                e.preventDefault();
                document
                  .getElementById("services")
                  ?.scrollIntoView({ behavior: "smooth" });
              }}
              className="group inline-flex items-center justify-center gap-2 border border-[#050505] px-8 py-4 text-sm uppercase tracking-[0.14em] text-[#050505] transition-colors duration-300 hover:bg-[#050505] hover:text-white"
            >
              Explore Services
              <ArrowDown size={16} className="transition-transform duration-300 group-hover:translate-y-1" />
            </a>
          </motion.div>
        </div>
      </section>

      {/* ---------------- SERVICES (grid, same as /services) ---------------- */}
      <section id="services" className="scroll-mt-[120px] border-t border-black/10">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-20 md:py-28">
          <div className="grid grid-cols-1 gap-10 md:grid-cols-2">
            {SERVICES.map((s, i) => (
              <Reveal key={s.slug} delay={(i % 2) * 0.08}>
                <Link
                  to={`/services/${s.slug}`}
                  data-testid={`home-service-${s.slug}`}
                  className="group block"
                >
                  <div className="relative aspect-[16/10] overflow-hidden rounded-2xl bg-[#f5f5f4]">
                    <img
                      src={s.image}
                      alt={s.title}
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute right-4 top-4 flex h-11 w-11 items-center justify-center bg-white/90 backdrop-blur opacity-0 translate-y-2 transition-all duration-300 group-hover:opacity-100 group-hover:translate-y-0">
                      <ArrowUpRight size={18} />
                    </div>
                  </div>
                  <div className="mt-5 flex items-baseline justify-between border-t border-black/12 pt-5">
                    <h3 className="font-serif-display text-3xl md:text-4xl tracking-tight">
                      {s.title}
                    </h3>
                    <span className="text-xs text-[#999]">0{i + 1}</span>
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-[#3d3d3d]">{s.tagline}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- OUR PHILOSOPHY (DARK) ---------------- */}
      <section className="relative bg-[#050505] text-white grain overflow-hidden">
        <div className="relative mx-auto max-w-[1400px] px-5 md:px-10 py-24 md:py-32">
          <div className="grid gap-10 lg:grid-cols-12">
            <div className="lg:col-span-4">
              <Reveal>
                <Label dark>Our Philosophy</Label>
              </Reveal>
            </div>
            <div className="lg:col-span-8">
              <Reveal delay={0.05}>
                <p className="font-serif-display text-3xl sm:text-4xl md:text-5xl font-normal leading-[1.2] tracking-tight text-white">
                  We focus on enhancing your natural features — blending modern technology with
                  gentle, personalised care in a peaceful, professional environment.
                </p>
              </Reveal>
              <Reveal delay={0.15}>
                <Link
                  to="/our-story"
                  className="mt-10 inline-flex items-center gap-2 border border-white px-8 py-4 text-sm uppercase tracking-[0.14em] text-white transition-colors duration-300 hover:bg-white hover:text-[#050505]"
                >
                  Read our story <ArrowRight size={15} />
                </Link>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- TEAM TEASER ---------------- */}
      <section className="mx-auto max-w-[1400px] px-5 md:px-10 py-24 md:py-32">
        <div className="mb-12 flex flex-col items-center text-center">
          <Label className="mb-4">Meet The Team</Label>
          <h2 className="font-serif-display text-4xl md:text-6xl tracking-tight">Our Team</h2>
        </div>
        <div className="grid grid-cols-2 gap-6 sm:grid-cols-3 sm:gap-8">
          {TEAM.map((m, i) => (
            <Reveal key={m.name} delay={(i % 3) * 0.1}>
              <div className="group">
                <div className="relative aspect-[4/5] overflow-hidden rounded-2xl">
                  <img
                    src={m.image}
                    alt={m.name}
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <h3 className="mt-5 font-serif-display text-2xl">{m.name}</h3>
                <p className="text-[11px] uppercase tracking-[0.16em] text-[#888]">{m.role}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <div className="mt-12 text-center">
          <Link
            to="/team"
            className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.15em] link-underline"
          >
            Meet the full team <ArrowRight size={15} />
          </Link>
        </div>
      </section>

      {/* ---------------- OFFERS (LIGHT + CHECKERBOARD) ---------------- */}
      <section className="border-t border-black/10">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-24 md:py-32">
          <div className="mb-14 flex flex-col items-center text-center">
            <Label className="mb-4">Special</Label>
            <h2 className="font-serif-display text-4xl md:text-6xl tracking-tight">Offers & Rewards</h2>
          </div>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {OFFERS.map((o, i) => {
              const Icon = OFFER_ICONS[i % OFFER_ICONS.length];
              return (
                <Reveal key={o.title} delay={(i % 2) * 0.08}>
                  <div
                    data-testid={`home-offer-${i}`}
                    className="group flex h-full flex-col rounded-2xl border border-black/10 bg-white p-10 md:p-12 transition-all duration-300 hover:-translate-y-1 hover:border-black/20 hover:shadow-[0_24px_60px_-24px_rgba(0,0,0,0.28)]"
                  >
                    <div className="flex items-center justify-between">
                      <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[#050505] text-white transition-transform duration-300 group-hover:scale-105">
                        <Icon size={24} strokeWidth={1.4} />
                      </span>
                      <span className="inline-block rounded-full bg-[#050505] px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-white">
                        {o.tag}
                      </span>
                    </div>
                    <h3 className="mt-8 font-serif-display text-3xl md:text-4xl tracking-tight text-[#050505]">
                      {o.title}
                    </h3>
                    <p className="mt-4 max-w-md text-base leading-relaxed text-[#3d3d3d]">
                      {o.detail}
                    </p>
                    <a
                      href={BUSINESS.bookingUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-8 inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-[#050505]"
                    >
                      Claim offer
                      <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                    </a>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ---------------- CTA ---------------- */}
      <section className="border-t border-black/10">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-24 md:py-36 text-center">
          <Reveal>
            <div className="mb-5 flex justify-center">
              <Label>Ready To Glow?</Label>
            </div>
            <h2 className="mx-auto max-w-3xl font-serif-display text-5xl md:text-7xl font-normal tracking-tight leading-[0.98]">
              Book your personalised beauty experience.
            </h2>
            <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <a
                href={BUSINESS.bookingUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="home-cta-book"
                className="group inline-flex items-center justify-center gap-2 bg-[#050505] px-10 py-4 text-sm uppercase tracking-[0.14em] text-white transition-colors duration-300 hover:bg-white hover:text-[#050505] border border-[#050505]"
              >
                Book Now
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </a>
              <a
                href={BUSINESS.phoneHref}
                className="inline-flex items-center justify-center gap-2 border border-[#050505] px-10 py-4 text-sm uppercase tracking-[0.14em] transition-colors duration-300 hover:bg-[#050505] hover:text-white"
              >
                {BUSINESS.phoneDisplay}
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
